import {
	useMutation,
	useQueryClient,
	type QueryClient,
	type InfiniteData,
} from "@tanstack/react-query";
import { getNotificationRepository, getLogger } from "@/infrastructure/di";
import { queryKeys } from "../keys";
import { EntityType } from "../entityDependencies";
import type { NotificationListResponse } from "@/domain";
import { useCreateMutation } from "../createMutation";

type OptimisticContext = {
	previousData: { queryKey: unknown; data: unknown }[];
};

function createOptimisticHandlers(
	queryClient: QueryClient,
	orgId: string,
	transform: (
		uuid: string,
		data: NotificationListResponse,
	) => NotificationListResponse,
) {
	return {
		onMutate: async (uuid: string): Promise<OptimisticContext> => {
			await queryClient.cancelQueries({
				queryKey: queryKeys.notifications.all(orgId),
			});
			const listQueries = queryClient.getQueryCache().findAll({
				queryKey: queryKeys.notifications.lists(orgId),
			});
			const previousData: OptimisticContext["previousData"] = [];
			for (const query of listQueries) {
				const data = query.state.data as
					| InfiniteData<NotificationListResponse>
					| undefined;
				if (data?.pages) {
					previousData.push({ queryKey: query.queryKey, data });
					queryClient.setQueryData(query.queryKey, {
						...data,
						pages: data.pages.map((page) => transform(uuid, page)),
						pageParams: data.pageParams,
					});
				}
			}
			return { previousData };
		},
		onError: (err: unknown, _uuid: string, context?: OptimisticContext) => {
			getLogger().error("Notification mutation failed:", err);
			if (context?.previousData) {
				for (const { queryKey, data } of context.previousData) {
					queryClient.setQueryData(queryKey, data);
				}
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.notifications.stats(orgId),
			});
		},
	};
}

export const useMarkNotificationRead = (orgId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (uuid: string) => getNotificationRepository().markAsRead(uuid),
		...createOptimisticHandlers(queryClient, orgId, (uuid, data) => ({
			...data,
			notifications: data.notifications.map((n) =>
				n.uuid === uuid
					? { ...n, is_read: true, read_at: new Date().toISOString() }
					: n,
			),
		})),
	});
};

export const useMarkNotificationUnread = (orgId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (uuid: string) =>
			getNotificationRepository().markAsUnread(uuid),
		...createOptimisticHandlers(queryClient, orgId, (uuid, data) => ({
			...data,
			notifications: data.notifications.map((n) =>
				n.uuid === uuid ? { ...n, is_read: false, read_at: undefined } : n,
			),
		})),
	});
};

export const useMarkAllNotificationsRead = (orgId: string) =>
	useCreateMutation({
		mutationFn: () => getNotificationRepository().markAllAsRead(),
		invalidates: { entities: [EntityType.NOTIFICATION], orgId },
		errorLogMessage: "Failed to mark all notifications as read:",
	});

export const useDeleteNotification = (orgId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (uuid: string) =>
			getNotificationRepository().deleteNotification(uuid),
		...createOptimisticHandlers(queryClient, orgId, (uuid, data) => ({
			...data,
			notifications: data.notifications.filter((n) => n.uuid !== uuid),
			total_count: Math.max(0, data.total_count - 1),
		})),
	});
};

export const useDeleteAllNotifications = (orgId: string) =>
	useCreateMutation({
		mutationFn: () => getNotificationRepository().deleteAllNotifications(),
		invalidates: { entities: [EntityType.NOTIFICATION], orgId },
		errorLogMessage: "Failed to delete all notifications:",
	});
